import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CreditCardIcon,
  CalendarIcon,
  UserIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  BanknotesIcon,
  QuestionMarkCircleIcon,
  ArrowDownTrayIcon,
  BuildingLibraryIcon,
  PencilSquareIcon,
  XCircleIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';

export default function VoirService() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpMessage, setHelpMessage] = useState('');
  const [sendingHelp, setSendingHelp] = useState(false);
  const [helpSuccess, setHelpSuccess] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showPaymentDetail, setShowPaymentDetail] = useState(null); // null, 'initial', ou index de modification
  const [nextUnafExportDate, setNextUnafExportDate] = useState(null);

  // Détecter si une modification est en attente de paiement
  const pendingModification = service?.historiqueModifications?.find(m => m.paiement?.status !== 'paye');
  const pendingModificationIndex = service?.historiqueModifications?.findIndex(m => m.paiement?.status !== 'paye');
  
  // Détecter si une modification est payée mais en attente de validation admin
  const pendingValidationModification = service?.historiqueModifications?.find(m => m.paiement?.status === 'paye' && !m.validated);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchService();
    fetchNextUnafExportDate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id, navigate]);

  const fetchNextUnafExportDate = async () => {
    try {
      const currentYear = new Date().getFullYear();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/public/next-unaf-export?annee=${currentYear}`);
      setNextUnafExportDate(response.data.nextExportDateFormatted);
    } catch (err) {
      console.error('Erreur lors du chargement de la prochaine date d\'export UNAF:', err);
    }
  };

  const fetchService = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/services/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setService(response.data);
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement du service');
    } finally {
      setLoading(false);
    }
  };

  const handleSendHelp = async (e) => {
    e.preventDefault();
    setSendingHelp(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/services/${id}/demande-aide`,
        { message: helpMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setHelpSuccess(true);
      setTimeout(() => {
        setShowHelpModal(false);
        setHelpSuccess(false);
        setHelpMessage('');
      }, 2000);
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de l'envoi du message");
    } finally {
      setSendingHelp(false);
    }
  };

  const handleOpenDocument = async (s3Key, fallbackUrl) => {
    if (!s3Key) {
      if (fallbackUrl) window.open(fallbackUrl, '_blank');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/files/signed-url/${encodeURIComponent(s3Key)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(response.data.url, '_blank');
    } catch (err) {
      console.error('Erreur ouverture document:', err);
      if (fallbackUrl) {
        window.open(fallbackUrl, '_blank');
      } else {
        alert('Erreur lors de l\'ouverture du document');
      }
    }
  };

  const handlePayment = async () => {
    try {
      setProcessingPayment(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/payment/service/create-payment-session/${service._id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Rediriger vers la page de paiement Stripe
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      console.error('Erreur lors de la création de la session de paiement:', err);
      setError(err.response?.data?.message || 'Erreur lors de la création de la session de paiement');
      setProcessingPayment(false);
    }
  };

  const handleModificationPayment = async () => {
    if (pendingModificationIndex === undefined || pendingModificationIndex < 0) return;
    
    try {
      setProcessingPayment(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/payment/service-modification/${service._id}/create-session`,
        { historiqueEntryIndex: pendingModificationIndex },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      console.error('Erreur lors de la création de la session de paiement modification:', err);
      setError(err.response?.data?.message || 'Erreur lors de la création de la session de paiement');
      setProcessingPayment(false);
    }
  };

  const getStatusConfig = (status, typeService) => {
    const configs = {
      en_attente_paiement: {
        label: 'En attente de paiement',
        color: 'bg-red-100 text-red-800',
        icon: ClockIcon,
        description: 'Votre paiement est en attente de traitement.',
      },
      en_attente_caution: {
        label: 'En attente de caution',
        color: 'bg-red-100 text-red-800',
        icon: ExclamationTriangleIcon,
        description: 'Votre paiement a été reçu. N\'oubliez pas de transmettre votre chèque de caution. Votre attestation d\'adhésion sera créée une fois le chèque de caution transmis.',
      },
      actif: {
        label: 'Actif',
        color: 'bg-green-100 text-green-800',
        icon: CheckCircleIcon,
        description: typeService === 'assurance_unaf' 
          ? 'Votre souscription aux services de l\'UNAF est active.'
          : 'Votre souscription est active. Vous pouvez utiliser les services.',
      },
      en_attente_validation: {
        label: 'En attente de validation',
        color: 'bg-red-100 text-red-800',
        icon: ClockIcon,
        description: typeService === 'assurance_unaf' && nextUnafExportDate
          ? `Votre souscription sera validée lors de la prochaine transmission à l'UNAF prévue le ${nextUnafExportDate}. Une attestation d'adhésion sera générée une fois votre souscription validée.`
          : 'Votre souscription est en attente de validation. Une attestation d\'adhésion sera générée une fois votre souscription validée.',
      },
      expire: {
        label: 'Expiré',
        color: 'bg-gray-100 text-gray-800',
        icon: ClockIcon,
        description: 'Votre souscription a expiré.',
      },
    };
    
    // Pour UNAF, si le statut est "en_attente_caution" c'est une erreur, traiter comme "en_attente_paiement"
    if (typeService === 'assurance_unaf' && status === 'en_attente_caution') {
      return configs.en_attente_paiement;
    }
    
    return configs[status] || configs.en_attente_paiement;
  };

  const getCautionStatusConfig = (status) => {
    const configs = {
      en_attente: {
        label: 'En attente',
        color: 'bg-honey-100 text-honey-800',
      },
      recu: {
        label: 'Reçu',
        color: 'bg-green-100 text-green-800',
      },
      rendu: {
        label: 'Rendu',
        color: 'bg-blue-100 text-blue-800',
      },
    };
    return configs[status] || configs.en_attente;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-honey-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-soft text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Erreur</h2>
          <p className="text-slate-600 mb-4">{error}</p>
          <Link
            to="/mes-adhesions"
            className="inline-flex items-center px-4 py-2 bg-honey-500 text-white rounded-lg hover:bg-honey-600 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Retour à mes adhésions
          </Link>
        </div>
      </div>
    );
  }

  if (!service) {
    return null;
  }

  const statusConfig = getStatusConfig(service.status, service.typeService);
  const StatusIcon = statusConfig.icon;
  const cautionConfig = getCautionStatusConfig(service.caution?.status);

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/mes-adhesions"
            className="inline-flex items-center text-honey-600 hover:text-honey-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Retour à mes adhésions
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold text-slate-900">
                  {service.nom}
                </h1>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-lg font-bold bg-honey-500 text-white shadow-sm">
                  {service.annee}
                </span>
              </div>
              <p className="text-xs text-slate-600 mt-1">
                Référence : {service._id}
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Bouton modifier/payer UNAF */}
              {service.typeService === 'assurance_unaf' && service.status === 'actif' && (
                pendingModification ? (
                  <button
                    onClick={handleModificationPayment}
                    disabled={processingPayment}
                    className="flex items-center px-4 py-2 bg-honey-500 text-white rounded-lg hover:bg-honey-600 transition-colors disabled:opacity-50"
                  >
                    <CreditCardIcon className="h-5 w-5 mr-2" />
                    {processingPayment ? 'Redirection...' : 'Payer la modification'}
                  </button>
                ) : !pendingValidationModification && (
                  <Link
                    to={`/service/${service._id}/modifier-unaf`}
                    className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <PencilSquareIcon className="h-5 w-5 mr-2" />
                    Modifier ma souscription
                  </Link>
                )
              )}
              <button
                onClick={() => setShowHelpModal(true)}
                className="flex items-center px-4 py-2 bg-white border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <QuestionMarkCircleIcon className="h-5 w-5 mr-2" />
                Besoin d'aide
              </button>
            </div>
          </div>
        </div>

        {/* Alerte modification en attente de paiement */}
        {service.typeService === 'assurance_unaf' && pendingModification && (
          <div className="bg-honey-100 text-honey-800 rounded-xl p-6 mb-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h2 className="text-lg font-semibold">Modification en attente de paiement</h2>
                  <p className="text-sm mt-1 opacity-90">
                    Vous avez une modification de souscription en attente. Montant à régler : <strong>{pendingModification.montantSupplementaire?.toFixed(2)} €</strong>
                  </p>
                </div>
              </div>
              <button
                onClick={handleModificationPayment}
                disabled={processingPayment}
                className="flex items-center px-4 py-2 bg-white text-honey-700 font-medium rounded-lg shadow-sm hover:bg-honey-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CreditCardIcon className="h-5 w-5 mr-2" />
                {processingPayment ? 'Redirection...' : 'Payer maintenant'}
              </button>
            </div>
          </div>
        )}

        {/* Alerte modification payée en attente de validation */}
        {service.typeService === 'assurance_unaf' && pendingValidationModification && (
          <div className="bg-honey-100 text-honey-800 rounded-xl p-6 mb-6">
            <div className="flex items-start">
              <ClockIcon className="h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-lg font-semibold">Modification en attente de validation</h2>
                <p className="text-sm mt-1 opacity-90">
                  Votre modification a été payée et sera validée lors du prochain envoi à l'UNAF. 
                  Vos nouveaux services seront alors activés et une nouvelle attestation sera générée.
                </p>
                <div className="mt-2 text-sm">
                  <strong>Modification demandée :</strong>
                  {pendingValidationModification.modifications?.formuleApres && pendingValidationModification.modifications.formuleApres !== pendingValidationModification.modifications.formuleAvant && (
                    <span className="ml-2">Formule {pendingValidationModification.modifications.formuleAvant?.replace('formule', '')} → {pendingValidationModification.modifications.formuleApres?.replace('formule', '')}</span>
                  )}
                  {pendingValidationModification.modifications?.affairesJuridiquesApres !== undefined && pendingValidationModification.modifications.affairesJuridiquesApres !== pendingValidationModification.modifications.affairesJuridiquesAvant && (
                    <span className="ml-2">• Affaires juridiques : {pendingValidationModification.modifications.affairesJuridiquesApres ? 'Ajouté' : 'Retiré'}</span>
                  )}
                  {pendingValidationModification.modifications?.ecocontributionApres !== undefined && pendingValidationModification.modifications.ecocontributionApres !== pendingValidationModification.modifications.ecocontributionAvant && (
                    <span className="ml-2">• Écocontribution : {pendingValidationModification.modifications.ecocontributionApres ? 'Ajoutée' : 'Retirée'}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Statut global */}
        <div className={`${statusConfig.color} rounded-xl p-6 mb-6`}>
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              <StatusIcon className="h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-lg font-semibold">{statusConfig.label}</h2>
                <p className="text-sm mt-1 opacity-90">{statusConfig.description}</p>
              </div>
            </div>
            {(service.status === 'en_attente_paiement' || 
              (service.typeService === 'assurance_unaf' && service.status === 'en_attente_caution')) && (
              <button
                onClick={handlePayment}
                disabled={processingPayment}
                className="flex items-center px-4 py-2 bg-white text-red-700 font-medium rounded-lg shadow-sm hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CreditCardIcon className="h-5 w-5 mr-2" />
                {processingPayment ? 'Redirection...' : 'Payer maintenant'}
              </button>
            )}
          </div>
        </div>

        {/* Informations principales */}
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-6 mb-6">

          {/* Card service */}
          {/* <div className="bg-white rounded-xl shadow-soft p-6">
            <div className="flex items-center mb-4">
              <WrenchScrewdriverIcon className="h-5 w-5 text-honey-600 mr-2" />
              <h2 className="text-lg font-semibold text-slate-900">Service</h2>
            </div>
            <div className="flex flex-col gap-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-500">Type de service</label>
                <p className="text-slate-900 font-medium capitalize">{service.nom}</p>
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-500">Organisme</label>
                <p className="text-slate-900">{service.organisme}</p>
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-500">Année</label>
                <p className="text-slate-900">{service.annee}</p>
              </div>
              {service.dateValidation && (
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-500">Activé le</label>
                  <p className="text-slate-900">
                    {new Date(service.dateValidation).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                </div>
              )}
            </div>
          </div> */}

          {/* Card caution - Seulement pour les services avec caution (pas UNAF) */}
          {service.typeService !== 'assurance_unaf' && (
            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex items-center mb-4">
                <BanknotesIcon className="h-5 w-5 text-honey-600 mr-2" />
                <h2 className="text-lg font-semibold text-slate-900">Caution</h2>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-500">Statut</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cautionConfig.color}`}>
                    {cautionConfig.label}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-500">Montant</label>
                  <p className="text-2xl font-bold text-slate-700">{service.caution?.montant} €</p>
                </div>
                {service.caution?.dateReception && (
                  <div>
                    <label className="text-sm font-medium text-slate-500">Date de réception</label>
                    <p className="text-slate-900">
                      {new Date(service.caution.dateReception).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                )}
                {service.caution?.dateRendu && (
                  <div>
                    <label className="text-sm font-medium text-slate-500">Date de rendu</label>
                    <p className="text-slate-900">
                      {new Date(service.caution.dateRendu).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                )}
                {service.caution?.note && (
                  <div>
                    <label className="text-sm font-medium text-slate-500">Note</label>
                    <p className="text-slate-900">{service.caution.note}</p>
                  </div>
                )}
              </div>

              {/* Alerte caution en attente - Seulement pour les services avec caution */}
              {service.status === 'en_attente_caution' && service.typeService !== 'assurance_unaf' && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
                  <div className="flex items-start">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-semibold text-red-800">Caution requise</h3>
                      <p className="text-sm text-red-700 mt-1">
                        Votre paiement du droit d'usage a été reçu. Pour activer votre accès aux services de la miellerie, 
                        veuillez remettre votre chèque de caution de <strong>{service.caution?.montant}€</strong> au responsable lors de votre première visite, à défaut, l'accès à la miellerie ne pourra pas vous être accordé.
                      </p>
                      <p className="text-sm text-red-700 mt-2">
                        <strong>Important :</strong> Le chèque ne sera pas encaissé. Il sera conservé et vous sera rendu en début d'année suivante
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Card détails UNAF - Seulement pour le service UNAF */}
          {service.typeService === 'assurance_unaf' && service.unafData && (
            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex items-center mb-4">
                <BuildingLibraryIcon className="h-5 w-5 text-green-600 mr-2" />
                <h2 className="text-lg font-semibold text-slate-900">Détails des services</h2>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-500">Nombre de ruches</label>
                  <span className="text-slate-900 font-medium">{service.unafData.nombreRuches}</span>
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-500">Nombre d'emplacements</label>
                  <span className="text-slate-900 font-medium">{service.unafData.nombreEmplacements}</span>
                </div>
                {service.unafData.siret && (
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-500">SIRET</label>
                    <span className="text-slate-900 font-medium">{service.unafData.siret}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-500">Formule assurance</label>
                  <span className="text-slate-900 font-medium capitalize">
                    {service.unafData.options?.assurance?.formule?.replace('formule', 'Formule ') || '-'}
                  </span>
                </div>
                {service.unafData.options?.revue?.choix && service.unafData.options.revue.choix !== 'aucun' && (
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-500">Revue</label>
                    <span className="text-slate-900 font-medium">
                      {service.unafData.options.revue.choix === 'papier_numerique' ? 'Papier + Numérique' : 
                       service.unafData.options.revue.choix.charAt(0).toUpperCase() + service.unafData.options.revue.choix.slice(1)}
                    </span>
                  </div>
                )}
                {service.unafData.options?.affairesJuridiques?.souscrit && (
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-500">Affaires juridiques</label>
                    <span className="text-green-600 font-medium">Oui</span>
                  </div>
                )}
                {service.unafData.options?.ecocontribution?.souscrit && (
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-500">Écocontribution</label>
                    <span className="text-green-600 font-medium">Oui</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Section Paiements - inclut paiement initial + modifications */}
        <div className="bg-white rounded-xl shadow-soft p-6 mb-6">
          <div className="flex items-center mb-4">
            <CreditCardIcon className="h-5 w-5 text-honey-600 mr-2" />
            <h2 className="text-lg font-semibold text-slate-900">Paiements</h2>
          </div>
          <div className="space-y-4">
            {/* Paiement initial */}
            <div className={`border rounded-lg p-4 ${(service.paiement?.status === 'paye' && service.paiement?.exportedToUNAF) ? 'border-green-200 bg-green-50' : 'border-honey-200 bg-honey-50'}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-700">
                  Souscription initiale du 
                  {service.paiement?.datePaiement && (
                    <span> {new Date(service.paiement.datePaiement).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                  )}
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${(service.paiement?.status === 'paye' && service.paiement?.exportedToUNAF) ? 'bg-green-100 text-green-800' : 'bg-honey-100 text-honey-800'}`}>
                  {service.paiement?.status === 'paye' ? 'Payé' : 'En attente'}
                </span>
              </div>
              {service.typeService === 'assurance_unaf' && service.unafData?.optionsInitiales && (
                <div className="text-sm space-y-1 mb-3">
                  <p className="text-slate-600">
                    <span className="font-medium">Formule :</span> {service.unafData.optionsInitiales.assurance?.formule?.replace('formule', 'Formule ') || '-'}
                  </p>
                  {service.unafData.optionsInitiales.revue?.choix && service.unafData.optionsInitiales.revue.choix !== 'aucun' && (
                    <p className="text-slate-600">
                      <span className="font-medium">Revue :</span> {service.unafData.optionsInitiales.revue.choix === 'papier_numerique' ? 'Papier + Numérique' : service.unafData.optionsInitiales.revue.choix}
                    </p>
                  )}
                  {service.unafData.optionsInitiales.affairesJuridiques?.souscrit && (
                    <p className="text-slate-600"><span className="font-medium">Affaires juridiques</span></p>
                  )}
                  {service.unafData.optionsInitiales.ecocontribution?.souscrit && (
                    <p className="text-slate-600"><span className="font-medium">Écocontribution</span></p>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                <div className="text-sm text-slate-500">
                  {service.paiement?.typePaiement && (
                    <span className="capitalize">{service.paiement.typePaiement === 'en_ligne' ? 'En ligne' : 'Chèque'}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-honey-600">{service.paiement?.montant?.toFixed(2)} €</span>
                  {service.typeService === 'assurance_unaf' && service.unafData?.detailMontants && (
                    <button
                      onClick={() => setShowPaymentDetail('initial')}
                      className="p-1 text-slate-400 hover:text-honey-600 hover:bg-honey-50 rounded-full transition-colors"
                      title="Voir le détail du montant"
                    >
                      <QuestionMarkCircleIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Modifications UNAF */}
            {service.typeService === 'assurance_unaf' && service.historiqueModifications && service.historiqueModifications.map((modif, index) => (
              <div key={index} className={`border rounded-lg p-4 ${modif?.exportedToUNAF === true ? 'border-green-200 bg-green-50' : 'border-honey-200 bg-honey-50'}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-slate-700">
                    Modification du {new Date(modif.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${modif?.exportedToUNAF === true ? 'bg-green-100 text-green-800' : 'bg-honey-100 text-honey-800'}`}>
                    {modif?.exportedToUNAF === true ? 'Payé' : modif?.exportedToUNAF === false ? 'En attente de validation' : 'En attente'}
                  </span>
                </div>
                <div className="text-sm space-y-1">
                  {modif.nouvellesOptions?.assurance?.formule && modif.nouvellesOptions.assurance.formule !== modif.anciennesOptions?.assurance?.formule && (
                    <p className="text-slate-600">
                      <span className="font-medium">Formule :</span> {modif.anciennesOptions?.assurance?.formule?.replace('formule', 'Formule ')} → <span className="text-indigo-600 font-medium">{modif.nouvellesOptions.assurance.formule.replace('formule', 'Formule ')}</span>
                    </p>
                  )}
                  {modif.nouvellesOptions?.revue?.choix && modif.nouvellesOptions.revue.choix !== 'aucun' && (!modif.anciennesOptions?.revue?.choix || modif.anciennesOptions.revue.choix === 'aucun') && (
                    <p className="text-slate-600">
                      <span className="font-medium">Revue ajoutée :</span> <span className="text-indigo-600 font-medium">{modif.nouvellesOptions.revue.choix === 'papier_numerique' ? 'Papier + Numérique' : modif.nouvellesOptions.revue.choix}</span>
                    </p>
                  )}
                  {modif.nouvellesOptions?.affairesJuridiques?.souscrit && !modif.anciennesOptions?.affairesJuridiques?.souscrit && (
                    <p className="text-slate-600">
                      <span className="font-medium">Affaires juridiques :</span> <span className="text-indigo-600 font-medium">Ajouté</span>
                    </p>
                  )}
                  {modif.nouvellesOptions?.ecocontribution?.souscrit && !modif.anciennesOptions?.ecocontribution?.souscrit && (
                    <p className="text-slate-600">
                      <span className="font-medium">Écocontribution :</span> <span className="text-indigo-600 font-medium">Ajouté</span>
                    </p>
                  )}
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                  <div className="text-sm text-slate-500">
                    {modif.paiement?.datePaiement && (
                      <span>Payé le {new Date(modif.paiement.datePaiement).toLocaleDateString('fr-FR')}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-indigo-600">+{modif.montantSupplementaire?.toFixed(2)} €</span>
                    <button
                      onClick={() => setShowPaymentDetail(index)}
                      className="p-1 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors"
                      title="Voir le détail du montant"
                    >
                      <QuestionMarkCircleIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Total */}
            {service.typeService === 'assurance_unaf' && service.historiqueModifications && service.historiqueModifications.length > 0 && (
              <div className="border-t-2 border-slate-300 pt-4 flex items-center justify-between">
                <span className="text-lg font-semibold text-slate-700">Total payé</span>
                <span className="text-2xl font-bold text-honey-600">
                  {((service.paiement?.montant || 0) + (service.historiqueModifications?.filter(m => m.paiement?.status === 'paye').reduce((sum, m) => sum + (m.montantSupplementaire || 0), 0) || 0)).toFixed(2)} €
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Informations personnelles */}
        {service.informationsPersonnelles && (
          <div className="bg-white rounded-xl shadow-soft p-6 mb-6">
            <div className="flex items-center mb-4">
              <UserIcon className="h-5 w-5 text-honey-600 mr-2" />
              <h2 className="text-lg font-semibold text-slate-900">Informations personnelles</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {(service.informationsPersonnelles.prenom || service.informationsPersonnelles.nom) && (
                <div className="flex items-start">
                  <UserIcon className="h-5 w-5 text-slate-400 mr-2 mt-0.5" />
                  <div>
                    <label className="text-sm font-medium text-slate-500">Nom complet</label>
                    <p className="text-slate-900">
                      {service.informationsPersonnelles.prenom} {service.informationsPersonnelles.nom}
                    </p>
                  </div>
                </div>
              )}
              {service.informationsPersonnelles.adresse && (
                <div className="flex items-start">
                  <MapPinIcon className="h-5 w-5 text-slate-400 mr-2 mt-0.5" />
                  <div>
                    <label className="text-sm font-medium text-slate-500">Adresse</label>
                    <p className="text-slate-900">
                      {service.informationsPersonnelles.adresse.rue}{', '}{service.informationsPersonnelles.adresse.codePostal} {service.informationsPersonnelles.adresse.ville}
                    </p>
                  </div>
                </div>
              )}
              {service.informationsPersonnelles.telephoneMobile && (
                <div className="flex items-start">
                  <PhoneIcon className="h-5 w-5 text-slate-400 mr-2 mt-0.5" />
                  <div>
                    <label className="text-sm font-medium text-slate-500">Téléphone</label>
                    <p className="text-slate-900">{service.informationsPersonnelles.telephoneMobile}</p>
                  </div>
                </div>
              )}
              {service.informationsPersonnelles.email && (
                <div className="flex items-start">
                  <EnvelopeIcon className="h-5 w-5 text-slate-400 mr-2 mt-0.5" />
                  <div>
                    <label className="text-sm font-medium text-slate-500">Email</label>
                    <p className="text-slate-900">{service.informationsPersonnelles.email}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Documents */}
        <div className="bg-white rounded-xl shadow-soft p-6 mb-6">
          <div className="flex items-center mb-4">
            <DocumentTextIcon className="h-5 w-5 text-honey-600 mr-2" />
            <h2 className="text-lg font-semibold text-slate-900">Documents</h2>
          </div>
          
          {(service.attestationKey || service.ecocontributionAttestationKey || service.reglementMiellerieKey) ? (
            <ul className="divide-y divide-slate-200">
              {/* Extrait du règlement intérieur miellerie (signé) */}
              {service.reglementMiellerieKey && (
                <li className="py-3 flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-slate-900">Extrait du règlement intérieur de la miellerie</p>
                    <p className="text-sm text-slate-500">Annexe au règlement intérieur de l&apos;AMAIR - Année {service.annee}</p>
                  </div>
                  <button
                    onClick={() => handleOpenDocument(service.reglementMiellerieKey, service.reglementMiellerieUrl)}
                    className="p-2 text-honey-600 hover:bg-honey-50 rounded-lg transition-colors"
                    title="Ouvrir l'extrait du règlement intérieur"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5" />
                  </button>
                </li>
              )}

              {/* Attestation de souscription */}
              {service.attestationKey && (
                <li className="py-3 flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-slate-900">Attestation de souscription</p>
                    <p className="text-sm text-slate-500">{service.nom} - Année {service.annee}</p>
                  </div>
                  <button
                    onClick={() => handleOpenDocument(service.attestationKey, service.attestationUrl)}
                    className="p-2 text-honey-600 hover:bg-honey-50 rounded-lg transition-colors"
                    title="Ouvrir l'attestation"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5" />
                  </button>
                </li>
              )}

              {/* Attestation Écocontribution (si souscrite) */}
              {service.ecocontributionAttestationKey && (
                <li className="py-3 flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-slate-900">Attestation Écocontribution</p>
                    <p className="text-sm text-slate-500">UNAF - Année {service.annee}</p>
                  </div>
                  <button
                    onClick={() => handleOpenDocument(service.ecocontributionAttestationKey, service.ecocontributionAttestationUrl)}
                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Ouvrir l'attestation écocontribution"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5" />
                  </button>
                </li>
              )}
            </ul>
          ) : (
            <p className="text-sm text-slate-500 italic">
              {service.status !== 'actif' && service.status !== 'en_attente_validation'
                ? 'Les documents seront disponibles une fois votre souscription activée.'
                : service.status === 'en_attente_validation'
                ? 'L\'attestation sera générée une fois votre souscription validée par l\'administration.'
                : 'Aucun document disponible.'}
            </p>
          )}
        </div>

        {/* Reçus de paiement */}
        {service.receiptUrl && (
          <div className="bg-white rounded-xl shadow-soft p-6 mb-6">
            <div className="flex items-center mb-4">
              <CreditCardIcon className="h-5 w-5 text-honey-600 mr-2" />
              <h2 className="text-lg font-semibold text-slate-900">Reçus de paiement</h2>
            </div>
            
            <ul className="divide-y divide-slate-200">
              {/* Reçu de paiement initial */}
              <li className="py-3 flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-slate-900">Reçu de paiement</p>
                  <p className="text-sm text-slate-500">Souscription initiale - {service.paiement?.datePaiement ? new Date(service.paiement.datePaiement).toLocaleDateString('fr-FR') : ''}</p>
                </div>
                <a
                  href={service.receiptUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                  title="Voir le reçu Stripe"
                >
                  <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                </a>
              </li>
            </ul>
          </div>
        )}

        {/* Règlement intérieur */}
        <div className="bg-white rounded-xl shadow-soft p-6 mb-6">
          <div className="flex items-center mb-4">
            <DocumentTextIcon className="h-5 w-5 text-honey-600 mr-2" />
            <h2 className="text-lg font-semibold text-slate-900">{ service.typeService === 'assurance_unaf' ? 'Conditions de souscription des services de l\'UNAF' : 'Règlement intérieur'}</h2>
          </div>
          <div className="flex items-center">
            {service.acceptationReglement ? (
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
            ) : (
              <ClockIcon className="h-5 w-5 text-honey-500 mr-2" />
            )}
            <span className="text-slate-700">
              {service.acceptationReglement ? 'Accepté' : 'En attente d\'acceptation'}
            </span>
          </div>
        </div>

        {/* Dates */}
        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center mb-4">
            <CalendarIcon className="h-5 w-5 text-honey-600 mr-2" />
            <h2 className="text-lg font-semibold text-slate-900">Historique</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-slate-500">Date de souscription</label>
              <p className="text-slate-900">
                {new Date(service.createdAt).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">Date de validation</label>
              <p className="text-slate-900">
                { service.dateValidation ?
                new Date(service.dateValidation).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                }) : '-' }
              </p>
            </div>
          </div>
        </div>

        {/* Lien vers l'adhésion liée */}
        {service.adhesion && (
          <div className="mt-6 text-center">
            <Link
              to={`/adhesion/${service.adhesion._id}`}
              className="inline-flex items-center text-honey-600 hover:text-honey-700 font-medium"
            >
              Voir l'adhésion {service.adhesion.organisme} {service.adhesion.annee} associée
              <ArrowLeftIcon className="h-4 w-4 ml-2 rotate-180" />
            </Link>
          </div>
        )}
      </div>

      {/* Modal d'aide */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-slate-900">
                Besoin d'aide ?
              </h3>
              <button
                onClick={() => setShowHelpModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            {helpSuccess ? (
              <div className="text-center py-8">
                <CheckCircleIcon className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <p className="text-green-600 font-medium">
                  Message envoyé avec succès !
                </p>
              </div>
            ) : (
              <form onSubmit={handleSendHelp}>
                <p className="text-slate-600 mb-4">
                  Décrivez votre problème ou votre question concernant ce service.
                  Un administrateur du groupement {service.organisme} vous répondra par email.
                </p>

                <textarea
                  value={helpMessage}
                  onChange={(e) => setHelpMessage(e.target.value)}
                  required
                  rows={5}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-honey-500 focus:border-honey-500"
                  placeholder="Décrivez votre demande..."
                />

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowHelpModal(false)}
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={sendingHelp}
                    className="flex-1 px-4 py-2 bg-honey-500 text-white rounded-lg hover:bg-honey-600 transition-colors disabled:bg-slate-400"
                  >
                    {sendingHelp ? 'Envoi...' : 'Envoyer'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Modal détail du paiement */}
      {showPaymentDetail !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-slate-900">
                Détail du montant
              </h3>
              <button
                onClick={() => setShowPaymentDetail(null)}
                className="text-slate-400 hover:text-slate-600"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Paiement initial */}
              {showPaymentDetail === 'initial' && service.unafData?.detailMontants && (
                <>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-slate-900">Souscription initiale</p>
                        <p className="text-sm text-slate-500">Services de l'UNAF - Année {service.annee}</p>
                      </div>
                      <p className="text-xl font-bold text-honey-600">
                        {service.paiement?.montant?.toFixed(2)} €
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Cotisation UNAF</span>
                      <span className="text-slate-900">{service.unafData.detailMontants.cotisationUNAF?.toFixed(2)} €</span>
                    </div>
                    
                    {service.unafData.detailMontants.assurance > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">
                          Assurance {service.unafData.options?.assurance?.formule?.replace('formule', 'Formule ')} ({service.unafData.nombreRuches} ruches × {service.unafData.options?.assurance?.prixParRuche?.toFixed(2)} €)
                        </span>
                        <span className="text-slate-900">{service.unafData.detailMontants.assurance?.toFixed(2)} €</span>
                      </div>
                    )}
                    
                    {service.unafData.detailMontants.revue > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">
                          Revue ({service.unafData.options?.revue?.choix === 'papier_numerique' ? 'Papier + Numérique' : service.unafData.options?.revue?.choix})
                        </span>
                        <span className="text-slate-900">{service.unafData.detailMontants.revue?.toFixed(2)} €</span>
                      </div>
                    )}
                    
                    {service.unafData.detailMontants.affairesJuridiques > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">
                          Affaires juridiques ({service.unafData.nombreRuches} ruches × {service.unafData.options?.affairesJuridiques?.prixParRuche?.toFixed(2)} €)
                        </span>
                        <span className="text-slate-900">{service.unafData.detailMontants.affairesJuridiques?.toFixed(2)} €</span>
                      </div>
                    )}
                    
                    {service.unafData.detailMontants.ecocontribution > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">
                          Écocontribution ({service.unafData.nombreRuches} ruches × {service.unafData.options?.ecocontribution?.prixParRuche?.toFixed(2)} €)
                        </span>
                        <span className="text-slate-900">{service.unafData.detailMontants.ecocontribution?.toFixed(2)} €</span>
                      </div>
                    )}

                    <div className="border-t border-slate-200 pt-2 mt-2 flex justify-between font-semibold">
                      <span className="text-slate-900">Total</span>
                      <span className="text-honey-600">{service.paiement?.montant?.toFixed(2)} €</span>
                    </div>
                  </div>
                </>
              )}

              {/* Paiement modification */}
              {typeof showPaymentDetail === 'number' && service.historiqueModifications?.[showPaymentDetail] && (() => {
                const modif = service.historiqueModifications[showPaymentDetail];
                const nombreRuches = service.unafData?.nombreRuches || 0;
                
                // Tarifs pour calculer le détail
                const TARIFS = {
                  assurance: { formule1: 0.10, formule2: 1.65, formule3: 2.80 },
                  revue: { papier: 31, numerique: 18, papier_numerique: 35 },
                  affairesJuridiques: 0.15,
                  ecocontribution: 0.12,
                };

                return (
                  <>
                    <div className="bg-indigo-50 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-semibold text-slate-900">Modification</p>
                          <p className="text-sm text-slate-500">
                            {new Date(modif.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </p>
                        </div>
                        <p className="text-xl font-bold text-indigo-600">
                          +{modif.montantSupplementaire?.toFixed(2)} €
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm">
                      {/* Changement de formule assurance */}
                      {modif.modifications?.formuleApres && modif.modifications.formuleApres !== modif.modifications.formuleAvant && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">
                            Assurance : {modif.modifications.formuleAvant?.replace('formule', 'F')} → {modif.modifications.formuleApres?.replace('formule', 'F')}
                            <span className="text-xs text-slate-400 block">
                              ({nombreRuches} ruches × {((TARIFS.assurance[modif.modifications.formuleApres] || 0) - (TARIFS.assurance[modif.modifications.formuleAvant] || 0)).toFixed(2)} €)
                            </span>
                          </span>
                          <span className="text-slate-900">
                            {(((TARIFS.assurance[modif.modifications.formuleApres] || 0) - (TARIFS.assurance[modif.modifications.formuleAvant] || 0)) * nombreRuches).toFixed(2)} €
                          </span>
                        </div>
                      )}

                      {/* Ajout revue */}
                      {modif.modifications?.revueApres && modif.modifications.revueApres !== 'aucun' && (!modif.modifications.revueAvant || modif.modifications.revueAvant === 'aucun') && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">
                            Revue ajoutée ({modif.modifications.revueApres === 'papier_numerique' ? 'Papier + Numérique' : modif.modifications.revueApres})
                          </span>
                          <span className="text-slate-900">{(TARIFS.revue[modif.modifications.revueApres] || 0).toFixed(2)} €</span>
                        </div>
                      )}

                      {/* Ajout affaires juridiques */}
                      {modif.modifications?.affairesJuridiquesApres && !modif.modifications.affairesJuridiquesAvant && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">
                            Affaires juridiques
                            <span className="text-xs text-slate-400 block">({nombreRuches} ruches × {TARIFS.affairesJuridiques.toFixed(2)} €)</span>
                          </span>
                          <span className="text-slate-900">{(TARIFS.affairesJuridiques * nombreRuches).toFixed(2)} €</span>
                        </div>
                      )}

                      {/* Ajout écocontribution */}
                      {modif.modifications?.ecocontributionApres && !modif.modifications.ecocontributionAvant && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">
                            Écocontribution
                            <span className="text-xs text-slate-400 block">({nombreRuches} ruches × {TARIFS.ecocontribution.toFixed(2)} €)</span>
                          </span>
                          <span className="text-slate-900">{(TARIFS.ecocontribution * nombreRuches).toFixed(2)} €</span>
                        </div>
                      )}

                      <div className="border-t border-slate-200 pt-2 mt-2 flex justify-between font-semibold">
                        <span className="text-slate-900">Total supplément</span>
                        <span className="text-indigo-600">+{modif.montantSupplementaire?.toFixed(2)} €</span>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            <button
              onClick={() => setShowPaymentDetail(null)}
              className="w-full mt-6 px-4 py-2 bg-honey-500 text-white rounded-lg hover:bg-honey-600 transition-colors"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
